import React, { useEffect, useRef, useState } from "react";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";

const CoinIcon = ({ symbol, iconPath, size = 26 }) => {
  const src = iconPath ? resolvePublicUploadUrl(iconPath) : "";
  if (src) {
    return (
      <img
        src={src}
        alt={symbol}
        width={size}
        height={size}
        className="otc-coin-icon-img"
      />
    );
  }
  switch (symbol) {
    case "USDT":
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#26A17B" />
          <path
            d="M17.922 17.383c-.11.008-.68.04-1.922.04-1.07 0-1.74-.03-1.907-.04-.415-.02-.82-.07-1.216-.146v2.793c0 .24.195.435.436.435h5.374a.436.436 0 00.435-.435v-2.793c-.395.076-.8.126-1.2.146zm2.846-5.83h-9.536a.436.436 0 00-.436.436v2.176c0 .24.196.435.436.435h3.047c.075.328.24.896.69 1.258.46.368 1.135.53 1.96.53.824 0 1.5-.162 1.96-.53.45-.362.615-.93.69-1.258h3.189a.436.436 0 00.436-.435v-2.176a.436.436 0 00-.436-.436z"
            fill="#FFFFFF"
          />
          <path
            d="M20.768 9.5H11.232a.436.436 0 00-.436.436v1.452c0 .24.196.435.436.435h9.536a.436.436 0 00.436-.435V9.936a.436.436 0 00-.436-.436z"
            fill="#FFFFFF"
          />
        </svg>
      );
    case "BTC":
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#F7931A" />
          <path
            d="M22.27 13.71c.32-2.13-1.3-3.27-3.51-4.03l.72-2.88-1.75-.44-.7 2.8c-.46-.11-.93-.22-1.4-.33l.71-2.83-1.75-.44-.72 2.87c-.38-.09-.76-.17-1.13-.26l-2.42-.6-.47 1.87s1.3.3 1.28.32c.71.18.84.65.82 1.03l-.82 3.3c.05.01.11.03.18.06l-.18-.04-1.15 4.62c-.09.22-.31.55-.81.42.02.03-1.28-.32-1.28-.32l-.87 2.01 2.28.57c.42.11.84.22 1.25.33l-.73 2.92 1.75.44.72-2.88c.48.13.95.25 1.41.36l-.71 2.86 1.75.44.73-2.92c2.99.57 5.23.34 6.18-2.36.76-2.18-.04-3.44-1.61-4.26 1.15-.26 2.01-1.02 2.24-2.58zm-4.01 5.63c-.54 2.18-4.22 1-5.41.71l.97-3.87c1.19.3 4.99.89 4.44 3.16zm.55-5.67c-.5 1.98-3.57.97-4.57.73l.87-3.5c1 .25 4.2.72 3.7 2.77z"
            fill="#FFFFFF"
          />
        </svg>
      );
    case "ETH":
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#627EEA" />
          <g fill="#FFFFFF">
            <path d="M16 4v9.237l7.797 3.486L16 4z" opacity="0.6" />
            <path d="M16 4L8.2 16.723l7.8 3.486V4z" />
            <path d="M16 21.43v6.567l7.803-10.743L16 21.43z" opacity="0.6" />
            <path d="M16 27.997v-6.568L8.2 17.254 16 27.997z" />
            <path d="M16 20.21l7.797-4.487L16 13.237v6.973z" opacity="0.2" />
            <path d="M8.2 15.723l7.8 4.487v-6.973l-7.8 2.486z" opacity="0.6" />
          </g>
        </svg>
      );
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="#D3B06C" />
          <text
            x="16"
            y="21"
            textAnchor="middle"
            fill="#FFFFFF"
            fontSize="15"
            fontWeight="800"
            fontFamily="sans-serif"
          >
            {String(symbol || "?").slice(0, 1)}
          </text>
        </svg>
      );
  }
};

const OtcCoinDropdown = ({ value, onChange, options = [], disabled, variant = "coin", align = "start" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedCoin = options.find((c) => c.symbol === value) || options[0] || {
    symbol: value || "—",
    name: "",
    iconPath: "",
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className={`creative-dropdown-wrapper ${isOpen ? "is-open" : ""}`}
      ref={dropdownRef}
    >
      <button
        type="button"
        className={`creative-dropdown-trigger ${variant === "pair" ? "is-pair" : ""} ${isOpen ? "open" : ""}`}
        disabled={disabled || !options.length}
        onClick={() => {
          const active = document.activeElement;
          if (active && active !== dropdownRef.current && typeof active.blur === "function") {
            active.blur();
          }
          setIsOpen((open) => !open);
        }}
      >
        <div className="coin-trigger-content">
          {variant === "coin" ? (
            <CoinIcon
              symbol={selectedCoin.symbol}
              iconPath={selectedCoin.iconPath}
              size={26}
            />
          ) : null}
          <span className="coin-symbol-text">{selectedCoin.symbol}</span>
        </div>
        <svg
          className={`dropdown-chevron ${isOpen ? "rotate" : ""}`}
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="#6B7280"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={`creative-dropdown-menu ${align === "end" ? "align-end" : ""} ${variant === "coin" ? "is-coin" : ""}`}>
          {options.map((coin) => {
            const isSelected = coin.symbol === value;
            return (
              <div
                key={coin.symbol}
                className={`creative-dropdown-item ${isSelected ? "selected" : ""}`}
                onClick={() => {
                  onChange(coin.symbol);
                  setIsOpen(false);
                }}
              >
                <div className="dropdown-item-left">
                  <CoinIcon symbol={coin.symbol} iconPath={coin.iconPath} size={28} />
                  <div className="coin-name-block">
                    <span className="coin-sym">{coin.symbol}</span>
                    <span className="coin-subname">{coin.name}</span>
                  </div>
                </div>
                {isSelected && (
                  <svg
                    className="check-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#D3B06C"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OtcCoinDropdown;
export { CoinIcon };
